import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowUpRight, GoogleLogo, SignOut } from '@phosphor-icons/react';
import { PageHero, Reveal } from '../lib/motion';
import { VoltButton } from '../components/site/FormBits';
import { API, track } from '../lib/api';
import { useSEO } from '../lib/seo';

const panelCls = 'border border-line bg-surface p-8 sm:p-10';

const AuthCallback = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const processed = useRef(false);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;
    const exchange = async () => {
      const next = new URLSearchParams(location.search).get('next');
      const safeNext = next && next.startsWith('/') && !next.startsWith('//') ? next : '/account';
      try {
        const sessionId = new URLSearchParams(location.hash.replace(/^#/, '')).get('session_id');
        if (!sessionId) throw new Error('Missing session id');
        const { data } = await axios.post(
          `${API}/auth/session`,
          { session_id: sessionId },
          { withCredentials: true }
        );
        track('conversion', { type: 'google_sign_in' });
        navigate(safeNext, { replace: true, state: { user: data.user } });
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Google sign-in could not be completed. Please try again.');
        const accountPath = safeNext === '/account' ? '/account' : `/account?next=${encodeURIComponent(safeNext)}`;
        navigate(accountPath, { replace: true, state: { authFailed: true } });
      }
    };
    exchange();
  }, [location.hash, location.search, navigate]);

  return (
    <div className="flex min-h-[70vh] items-center justify-center bg-ink px-6" data-testid="auth-callback">
      <p className="mono-label text-volt">Completing secure sign-in…</p>
    </div>
  );
};

const AccountView = ({ initialUser, authFailed }) => {
  const [user, setUser] = useState(initialUser || null);
  const [status, setStatus] = useState(initialUser ? 'signed-in' : 'checking');

  useEffect(() => {
    if (initialUser) return undefined;
    let active = true;
    axios
      .get(`${API}/auth/me`, { withCredentials: true })
      .then(({ data }) => {
        if (!active) return;
        setUser(data.user);
        setStatus('signed-in');
      })
      .catch(() => {
        if (!active) return;
        setUser(null);
        setStatus('signed-out');
      });
    return () => {
      active = false;
    };
  }, [initialUser]);

  const signIn = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const next = new URLSearchParams(window.location.search).get('next');
    const safeNext = next && next.startsWith('/') && !next.startsWith('//') ? next : '';
    const redirectUrl = window.location.origin + '/account' + (safeNext ? `?next=${encodeURIComponent(safeNext)}` : '');
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      setUser(null);
      setStatus('signed-out');
      toast.success('Signed out.');
    } catch {
      toast.error('Sign-out failed. Please try again.');
    }
  };

  if (status === 'checking') {
    return (
      <div className="flex min-h-[70vh] items-center justify-center bg-ink px-6" data-testid="account-loading">
        <p className="mono-label text-steel">Checking your session…</p>
      </div>
    );
  }

  return (
    <div data-testid="account-page">
      <PageHero
        eyebrow="Account"
        lines={status === 'signed-in' ? [<>You&rsquo;re signed in.</>] : [<>Sign in with</>, <span key="l2"><span className="text-volt">Google.</span></span>]}
        sub="One secure Google sign-in for buyer, provider and Vox Service Manager workspaces. Public RFQ and provider forms stay open — no account needed to submit."
      />
      <section className="bg-ink bg-grid">
        <div className="mx-auto max-w-3xl px-6 py-24 lg:px-10 lg:py-32">
          {status === 'signed-in' && user ? (
            <Reveal>
              <div className={panelCls} data-testid="account-signed-in">
                <div className="flex items-center gap-5">
                  {user.picture ? (
                    <img src={user.picture} alt={user.name || user.email} className="h-16 w-16 border border-line object-cover" data-testid="account-avatar" />
                  ) : (
                    <span className="flex h-16 w-16 items-center justify-center border border-line bg-ink font-display text-2xl font-black text-volt" data-testid="account-avatar-fallback">
                      {(user.name || user.email || 'V').slice(0, 1).toUpperCase()}
                    </span>
                  )}
                  <div className="min-w-0">
                    <p className="truncate font-display text-2xl font-bold text-parchment" data-testid="account-name">{user.name || 'Vox Meridian user'}</p>
                    <p className="truncate text-sm text-steel" data-testid="account-email">{user.email}</p>
                  </div>
                </div>
                <div className="mt-8 border border-line bg-ink p-5">
                  <p className="mono-label text-volt">Foundation release</p>
                  <p className="mt-3 text-sm leading-relaxed text-steel">
                    Your account is ready. Provider and buyer dashboards are not enabled yet — when they
                    launch, this Google account becomes your secure way in.
                  </p>
                </div>
                <div className="mt-8">
                  <Link to="/dashboard" data-testid="account-open-dashboard" className="flex w-full items-center justify-center gap-2 bg-volt px-5 py-3.5 font-mono text-xs font-bold uppercase tracking-[0.15em] text-ink transition-transform duration-200 hover:-translate-y-0.5 hover:bg-voltdark">
                    Open your dashboard <ArrowUpRight size={14} weight="bold" />
                  </Link>
                </div>
                <div className="mt-4 grid gap-3 sm:grid-cols-2">
                  <Link to="/buyers" data-testid="account-post-rfq-link" className="group flex items-center justify-center gap-2 bg-volt px-5 py-3.5 font-mono text-xs font-bold uppercase tracking-[0.15em] text-ink transition-transform duration-200 hover:-translate-y-0.5 hover:bg-voltdark">
                    Post an RFQ <ArrowUpRight size={14} weight="bold" />
                  </Link>
                  <Link to="/providers" data-testid="account-provider-link" className="group flex items-center justify-center gap-2 border border-line px-5 py-3.5 font-mono text-xs font-bold uppercase tracking-[0.15em] text-parchment transition-colors duration-200 hover:border-volt hover:text-volt">
                    Join as provider <ArrowUpRight size={14} weight="bold" />
                  </Link>
                </div>
                <button type="button" onClick={logout} data-testid="account-logout-button" className="mt-6 flex w-full items-center justify-center gap-2 border border-line px-5 py-3 font-mono text-xs font-bold uppercase tracking-[0.15em] text-steel transition-colors duration-200 hover:border-volt/60 hover:text-volt">
                  <SignOut size={15} /> Sign out
                </button>
              </div>
            </Reveal>
          ) : (
            <Reveal>
              <div className={panelCls} data-testid="account-signed-out">
                <p className="mono-label text-volt">Secure access</p>
                <h2 className="mt-4 font-display text-3xl font-black tracking-tight text-parchment">Use your Google account.</h2>
                <p className="mt-4 text-sm leading-relaxed text-steel">
                  No passwords to remember. Sessions last 7 days on this device. Submitting an RFQ or
                  provider application still works without an account. Vox Service Manager registration is free during the pilot.
                </p>
                {authFailed && (
                  <p className="mt-4 border border-volt/40 bg-volt/5 px-4 py-3 text-sm text-volt" data-testid="auth-error">
                    The last sign-in attempt did not complete. You can try again below.
                  </p>
                )}
                <VoltButton type="button" onClick={signIn} data-testid="google-signin-button" className="mt-8 w-full">
                  <span className="flex items-center justify-center gap-2"><GoogleLogo size={17} weight="bold" /> Continue with Google</span>
                </VoltButton>
              </div>
            </Reveal>
          )}
        </div>
      </section>
    </div>
  );
};

const Account = () => {
  useSEO({
    title: 'Account — Google Sign In | Vox Meridian',
    description: 'Sign in to Vox Meridian with Google. Secure account foundation for future provider and buyer dashboards; public forms remain open.',
    path: '/account',
    robots: 'noindex, nofollow',
  });
  const location = useLocation();
  if (location.hash?.includes('session_id=')) return <AuthCallback />;
  return <AccountView initialUser={location.state?.user} authFailed={!!location.state?.authFailed} />;
};

export default Account;
